import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CandidateEmploymentHistory {
  id: string;
  candidate_id: string;
  company_name: string;
  job_title: string;
  start_date: string | null;
  end_date: string | null;
  is_current_position: boolean;
  employment_type: string | null;
  salary_range: string | null;
  notes: string | null;
  job_id: string | null;
  client_id: string | null;
  placement_fee: number | null;
  placed_by_recruiter: boolean;
  created_at: string;
  updated_at: string;
}

export const useCandidateEmploymentHistory = (candidateId: string) => {
  return useQuery({
    queryKey: ["candidate-employment-history", candidateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_employment_history")
        .select("*")
        .eq("candidate_id", candidateId)
        .order("start_date", { ascending: false });

      if (error) {
        console.error("Error fetching employment history:", error);
        throw error;
      }

      return data as CandidateEmploymentHistory[];
    },
    enabled: !!candidateId,
  });
};

export const useAddEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (employmentData: Omit<CandidateEmploymentHistory, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("candidate_employment_history")
        .insert([employmentData])
        .select()
        .single();

      if (error) {
        console.error("Error adding employment history:", error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["candidate-employment-history", data.candidate_id] });
      toast.success("Employment history added successfully");
    },
    onError: (error) => {
      console.error("Failed to add employment history:", error);
      toast.error("Failed to add employment history");
    },
  });
};

export const useUpdateEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CandidateEmploymentHistory> & { id: string }) => {
      const { data, error } = await supabase
        .from("candidate_employment_history")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating employment history:", error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["candidate-employment-history", data.candidate_id] });
      toast.success("Employment history updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update employment history:", error);
      toast.error("Failed to update employment history");
    },
  });
};

export const useDeleteEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, candidateId }: { id: string; candidateId: string }) => {
      const { error } = await supabase
        .from("candidate_employment_history")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting employment history:", error);
        throw error;
      }

      return { id, candidateId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["candidate-employment-history", data.candidateId] });
      toast.success("Employment history deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete employment history:", error);
      toast.error("Failed to delete employment history");
    },
  });
};